import { useState, useCallback, useRef } from 'react'
import { useDatabases, useTables, useDBQuery, useDBExecute } from '@/hooks/useDBViewer'
import type { DBInfo, TableInfo } from '@/api/dbviewer'
import { Button, Card, EmptyState, Select, Table } from '@/admin-design/_common'

// Serialize SQL cell values to TSV text. Tabs/newlines inside values would
// break row alignment when pasted into Sheets/Excel, so we normalize them
// to spaces. NULL renders as an empty cell (spreadsheet-native convention).
function cellToTSV(v: unknown): string {
  if (v === null || v === undefined) return ''
  return String(v).replace(/\t/g, ' ').replace(/\r?\n/g, ' ')
}

function rowsToTSV(columns: string[], rows: unknown[][]): string {
  const header = columns.join('\t')
  const body = rows.map((r) => r.map(cellToTSV).join('\t')).join('\n')
  return rows.length === 0 ? header : `${header}\n${body}`
}

function rowsToJSON(columns: string[], rows: unknown[][]): string {
  return JSON.stringify(
    rows.map((r) => {
      const obj: Record<string, unknown> = {}
      columns.forEach((c, i) => {
        obj[c] = r[i] ?? null
      })
      return obj
    }),
    null,
    2,
  )
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // fall through to legacy path (insecure context, iframe sandbox, etc.)
  }
  const ta = document.createElement('textarea')
  ta.value = text
  ta.style.position = 'fixed'
  ta.style.opacity = '0'
  document.body.appendChild(ta)
  ta.select()
  const ok = document.execCommand('copy')
  document.body.removeChild(ta)
  return ok
}

export default function DBViewer() {
  const { data: databases, isLoading: dbLoading } = useDatabases()
  const [selectedService, setSelectedService] = useState<string>()
  const { data: tables, isLoading: tablesLoading } = useTables(selectedService)
  const [sql, setSql] = useState('')
  const queryMutation = useDBQuery()
  const execMutation = useDBExecute()
  const [execResult, setExecResult] = useState<{
    ok: boolean
    message: string
  } | null>(null)

  // copyKey = 'tsv' | 'json' | `cell:${i}:${j}` — identifies which control
  // recently flashed the "Copied!" feedback. Timer handle is kept so a
  // second click reuses the same slot (no flicker from overlapping timeouts).
  const [copied, setCopied] = useState<string | null>(null)
  const copyTimer = useRef<number | undefined>(undefined)
  const flashCopied = useCallback((key: string) => {
    setCopied(key)
    if (copyTimer.current) window.clearTimeout(copyTimer.current)
    copyTimer.current = window.setTimeout(() => setCopied(null), 1200)
  }, [])

  // Auto-route Execute: SELECT goes to the read endpoint (bounded to 100
  // rows by the backend), anything else (INSERT / UPDATE / DELETE / DDL)
  // goes to the write endpoint and reports rows-affected. One button,
  // no confirm — the SQL box is the confirmation.
  const runQuery = useCallback(
    (sqlOverride?: string) => {
      if (!selectedService) return
      const q = (sqlOverride ?? sql).trim()
      if (!q) return
      setExecResult(null)
      const isSelect = /^\s*select\b/i.test(q)
      if (isSelect) {
        queryMutation.mutate({ service: selectedService, sql: q, limit: 100 })
      } else {
        execMutation.reset()
        queryMutation.reset()
        execMutation.mutate(
          { service: selectedService, sql: q },
          {
            onSuccess: (res) => {
              setExecResult({
                ok: true,
                message:
                  res.last_insert_id > 0
                    ? `${res.rows_affected} row${res.rows_affected === 1 ? '' : 's'} affected · last_insert_id=${res.last_insert_id}`
                    : `${res.rows_affected} row${res.rows_affected === 1 ? '' : 's'} affected`,
              })
            },
            onError: (err: Error) => {
              setExecResult({ ok: false, message: err.message })
            },
          },
        )
      }
    },
    [selectedService, sql, queryMutation, execMutation],
  )

  const handleTableClick = useCallback(
    (table: TableInfo) => {
      const q = `SELECT * FROM "${table.name}" LIMIT 100`
      setSql(q)
      if (selectedService) {
        queryMutation.mutate({ service: selectedService, sql: q, limit: 100 })
      }
    },
    [selectedService, queryMutation],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault()
        runQuery()
      }
    },
    [runQuery],
  )

  const dbOptions = (databases ?? []).map((db: DBInfo) => ({
    value: db.service,
    label: `${db.service} (${db.size_mb.toFixed(1)} MB)`,
  }))

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-foreground">DB Viewer</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Read-only SQLite browser for SaaS service databases
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left sidebar: databases + tables */}
        <div className="space-y-3">
          {/* Database selector */}
          <Card className="p-3">
            <label className="block text-xs font-medium text-foreground mb-1.5">
              Database
            </label>
            {dbLoading ? (
              <div className="h-8 bg-muted rounded animate-pulse" />
            ) : (
              <Select
                value={selectedService ?? ''}
                onValueChange={(v) => {
                  setSelectedService(v || undefined)
                  setSql('')
                  queryMutation.reset()
                }}
                options={dbOptions}
                placeholder="Select a database..."
              />
            )}
          </Card>

          {/* Tables */}
          {selectedService && (
            <Card>
              <div className="px-3 py-2 border-b border-border">
                <h3 className="text-xs font-medium text-foreground">Tables</h3>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {tablesLoading ? (
                  <div className="p-3 space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="h-6 bg-muted rounded animate-pulse"
                      />
                    ))}
                  </div>
                ) : (tables ?? []).length === 0 ? (
                  <p className="p-3 text-xs text-muted-foreground">No tables found.</p>
                ) : (
                  <div className="divide-y divide-border">
                    {(tables ?? []).map((t: TableInfo) => (
                      <button
                        key={t.name}
                        type="button"
                        onClick={() => handleTableClick(t)}
                        className="w-full flex items-center justify-between px-3 py-1.5 text-sm hover:bg-muted/50 transition-colors text-left"
                      >
                        <span className="font-mono text-xs text-card-foreground truncate">
                          {t.name}
                        </span>
                        <span className="text-[10px] text-muted-foreground ml-2 shrink-0">
                          {t.row_count} rows
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>

        {/* Main area: SQL editor + results */}
        <div className="lg:col-span-3 space-y-3">
          {/* SQL Editor */}
          <Card className="p-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-foreground">
                SQL Query
              </label>
              <span className="text-[10px] text-muted-foreground">
                Ctrl+Enter to execute
              </span>
            </div>
            <textarea
              value={sql}
              onChange={(e) => setSql(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="SELECT * FROM ..."
              rows={4}
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm font-mono text-foreground shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring resize-y"
            />
            <div className="flex items-center flex-wrap gap-2 mt-2">
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => runQuery()}
                disabled={
                  !selectedService ||
                  !sql.trim() ||
                  queryMutation.isPending ||
                  execMutation.isPending
                }
                loading={queryMutation.isPending || execMutation.isPending}
              >
                {queryMutation.isPending || execMutation.isPending ? 'Running...' : 'Execute'}
              </Button>
              {queryMutation.isError && (
                <span className="text-xs text-destructive">
                  {queryMutation.error?.message ?? 'Query failed'}
                </span>
              )}
              {execResult && (
                <span
                  className={`text-xs px-2 py-0.5 rounded border ${
                    execResult.ok
                      ? 'border-success-soft bg-success-soft text-success-soft-foreground'
                      : 'border-destructive-soft bg-destructive-soft text-destructive-soft-foreground'
                  }`}
                >
                  {execResult.message}
                </span>
              )}
            </div>
          </Card>

          {/* Results */}
          {queryMutation.data && (
            <Card>
              <div className="flex items-center justify-between px-3 py-2 border-b border-border gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <h3 className="text-xs font-medium text-foreground">Results</h3>
                  <span className="text-[10px] text-muted-foreground">
                    {queryMutation.data.count} row
                    {queryMutation.data.count !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-6 px-2 text-[11px]"
                    title="Copy as TSV — paste into Sheets / Excel"
                    disabled={!queryMutation.data.rows}
                    onClick={async () => {
                      if (!queryMutation.data) return
                      const ok = await copyToClipboard(
                        rowsToTSV(
                          queryMutation.data.columns,
                          queryMutation.data.rows ?? [],
                        ),
                      )
                      if (ok) flashCopied('tsv')
                    }}
                  >
                    {copied === 'tsv' ? 'Copied!' : 'Copy TSV'}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-6 px-2 text-[11px]"
                    title="Copy as JSON array of objects"
                    disabled={!queryMutation.data.rows}
                    onClick={async () => {
                      if (!queryMutation.data) return
                      const ok = await copyToClipboard(
                        rowsToJSON(
                          queryMutation.data.columns,
                          queryMutation.data.rows ?? [],
                        ),
                      )
                      if (ok) flashCopied('json')
                    }}
                  >
                    {copied === 'json' ? 'Copied!' : 'Copy JSON'}
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <Table.Head>
                    <Table.Row className="hover:bg-transparent">
                      {queryMutation.data.columns.map((col) => (
                        <Table.Header key={col} className="whitespace-nowrap">
                          {col}
                        </Table.Header>
                      ))}
                    </Table.Row>
                  </Table.Head>
                  <Table.Body>
                    {(queryMutation.data.rows ?? []).map((row, i) => (
                      <Table.Row key={i}>
                        {row.map((cell, j) => {
                          const key = `cell:${i}:${j}`
                          const isCopied = copied === key
                          const display =
                            cell === null ? 'NULL' : String(cell)
                          return (
                            <Table.Cell
                              key={j}
                              onClick={async () => {
                                const ok = await copyToClipboard(
                                  cell === null ? '' : String(cell),
                                )
                                if (ok) flashCopied(key)
                              }}
                              className={`px-3 py-1.5 text-xs whitespace-nowrap max-w-xs truncate font-mono cursor-copy transition-colors ${
                                isCopied
                                  ? 'bg-success/10 text-success'
                                  : 'hover:bg-muted/60'
                              }`}
                              title={isCopied ? 'Copied!' : `Click to copy: ${display}`}
                            >
                              {cell === null ? (
                                <span className="text-muted-foreground italic">
                                  NULL
                                </span>
                              ) : (
                                String(cell)
                              )}
                            </Table.Cell>
                          )
                        })}
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
              {queryMutation.data.count === 0 && (
                <p className="p-4 text-xs text-muted-foreground text-center">
                  Query returned no results.
                </p>
              )}
            </Card>
          )}

          {!selectedService && (
            <Card>
              <EmptyState description="Select a database from the sidebar to start browsing." />
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
